/**
 * NMBRS Export — Generate CSV for salary administration
 */

import * as XLSX from 'xlsx';
import type { PayPeriod, StaffPeriodSummary } from './hours-calculator';

export interface NmbrsRow {
  medewerkernummer: string;
  naam: string;
  contract_type: string;
  gewerkte_uren: number;
  basisuurloon: number;
  toeslag_30: number;
  toeslag_50: number;
  toeslag_150: number;
  toeslag_totaal: number;
  vakantie_uren_opgebouwd: number;
  vakantie_uren_opgenomen: number;
  ziekte_uren: number;
  verlof_uren: number;
}

export interface NmbrsExportData {
  staffId: string;
  name: string;
  contractType: string;
  loonType: string;
  sfMedewerkerId: string | null;
  uurloon: number;
  // Vast salaris: bruto maandbedrag van de kaart; de export toont dan dit bedrag en laat het uurloon leeg
  maandsalaris?: number | null;
  summary: StaffPeriodSummary;
  vacationTaken: number;
  werkdagen: number;
  reiskostenvergoeding: number;
  yearSaldo: number | null;
  // Potje-mutatie velden (verplicht bij nieuwe export-flow)
  potjeBefore?: number;
  potjeMutation?: number; // negatief = opname, positief = storten
  potjeAfter?: number;
}

/**
 * Vast salaris: de accountant voert het maandbedrag in, niet uurloon × uren.
 * Alleen als de kaart het bedrag draagt (loonkaart-sync); anders valt de rij
 * terug op het afgeleide uurloon, zodat er nooit een lege loonregel ontstaat.
 */
function loonKolommen(d: NmbrsExportData): { uurloon: number | null; maandsalaris: number | null } {
  if (d.contractType === 'vast-salaris' && d.maandsalaris != null && d.maandsalaris > 0) {
    return { uurloon: null, maandsalaris: d.maandsalaris };
  }
  return { uurloon: d.uurloon, maandsalaris: null };
}

/**
 * Generate CSV content for NMBRS import
 */
export function generateNmbrsCsv(data: NmbrsExportData[], period: PayPeriod): string {
  const headers = [
    'Medewerkernummer',
    'Naam',
    'Contract',
    'Loontype',
    'Periode',
    'Uitbetaalde uren',
    'Basisuurloon',
    'Maandsalaris',
    'Toeslag 30% (uren)',
    'Toeslag 50% (uren)',
    'Toeslag 150% (uren)',
    'Toeslag totaal (uren)',
    'Vakantie-uren opgebouwd',
    'Vakantie-uren opgenomen',
    'Ziekte-uren',
    'Verlof-uren',
    'Werkdagen',
    'Reiskostenvergoeding',
    'Urensaldo (cumulatief)',
    'Potje voor',
    'Potje mutatie',
    'Potje na',
  ];

  const rows = data.map(d => {
    const hours30 = d.summary.surcharges.breakdown
      .filter(b => b.percentage === 0.30)
      .reduce((sum, b) => sum + b.hours, 0);
    const hours50 = d.summary.surcharges.breakdown
      .filter(b => b.percentage === 0.50)
      .reduce((sum, b) => sum + b.hours, 0);
    const hours150 = d.summary.surcharges.breakdown
      .filter(b => b.percentage === 1.50)
      .reduce((sum, b) => sum + b.hours, 0);

    const potjeBefore = d.potjeBefore ?? 0;
    const potjeMutation = d.potjeMutation ?? 0;
    const potjeAfter = d.potjeAfter ?? potjeBefore + potjeMutation;
    const loon = loonKolommen(d);

    return [
      d.sfMedewerkerId || d.staffId.slice(0, 8),
      d.name,
      d.contractType,
      d.loonType,
      period.label,
      d.summary.paidHours.toFixed(2),
      loon.uurloon != null ? loon.uurloon.toFixed(2) : '',
      loon.maandsalaris != null ? loon.maandsalaris.toFixed(2) : '',
      hours30.toFixed(2),
      hours50.toFixed(2),
      hours150.toFixed(2),
      (hours30 + hours50 + hours150).toFixed(2),
      d.summary.vacationAccrual.totalHours.toFixed(2),
      d.vacationTaken.toFixed(2),
      d.summary.sickHours.toFixed(2),
      d.summary.leaveHours.toFixed(2),
      d.werkdagen.toString(),
      d.reiskostenvergoeding.toFixed(2),
      d.yearSaldo !== null ? (d.yearSaldo >= 0 ? '+' : '') + d.yearSaldo.toFixed(2) : '',
      potjeBefore.toFixed(2),
      (potjeMutation >= 0 ? '+' : '') + potjeMutation.toFixed(2),
      potjeAfter.toFixed(2),
    ];
  });

  const csvLines = [
    headers.join(';'),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(';')),
  ];

  return csvLines.join('\n');
}

/**
 * Trigger CSV download in browser
 */
export function downloadCsv(csvContent: string, filename: string): void {
  // Add BOM for Excel compatibility
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Convert CSV content to base64 for API upload (to Supabase Storage).
 */
export function csvToBase64(csvContent: string): string {
  const bom = '\uFEFF';
  const full = bom + csvContent;
  // Browser-safe base64 of UTF-8 content
  if (typeof TextEncoder !== 'undefined') {
    const bytes = new TextEncoder().encode(full);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
    return btoa(binary);
  }
  return btoa(unescape(encodeURIComponent(full)));
}

/**
 * Genereer een echt .xlsx-bestand (OpenXML) via SheetJS — opent native
 * in Excel én Apple Numbers, geen HTML-truc.
 */
export function generateNmbrsExcelBlob(data: NmbrsExportData[], period: PayPeriod): Blob {
  const headers = [
    'Medewerkernummer', 'Naam', 'Contract', 'Loontype', 'Periode',
    'Uitbetaalde uren', 'Basisuurloon', 'Maandsalaris',
    'Toeslag 30% (uren)', 'Toeslag 50% (uren)', 'Toeslag 150% (uren)', 'Toeslag totaal (uren)',
    'Vakantie-uren opgebouwd', 'Vakantie-uren opgenomen',
    'Ziekte-uren', 'Verlof-uren', 'Werkdagen', 'Reiskostenvergoeding',
    'Urensaldo (cumulatief)', 'Potje voor', 'Potje mutatie', 'Potje na',
  ];

  const round2 = (n: number) => Math.round(n * 100) / 100;

  const rows = data.map(d => {
    const h30 = d.summary.surcharges.breakdown.filter(b => b.percentage === 0.30).reduce((s, b) => s + b.hours, 0);
    const h50 = d.summary.surcharges.breakdown.filter(b => b.percentage === 0.50).reduce((s, b) => s + b.hours, 0);
    const h150 = d.summary.surcharges.breakdown.filter(b => b.percentage === 1.50).reduce((s, b) => s + b.hours, 0);
    const potjeBefore = d.potjeBefore ?? 0;
    const potjeMutation = d.potjeMutation ?? 0;
    const potjeAfter = d.potjeAfter ?? potjeBefore + potjeMutation;
    const loon = loonKolommen(d);

    return [
      d.sfMedewerkerId || d.staffId.slice(0, 8),
      d.name,
      d.contractType,
      d.loonType,
      period.label,
      round2(d.summary.paidHours),
      loon.uurloon != null ? round2(loon.uurloon) : '',
      loon.maandsalaris != null ? round2(loon.maandsalaris) : '',
      round2(h30),
      round2(h50),
      round2(h150),
      round2(h30 + h50 + h150),
      round2(d.summary.vacationAccrual.totalHours),
      round2(d.vacationTaken),
      round2(d.summary.sickHours),
      round2(d.summary.leaveHours),
      d.werkdagen,
      round2(d.reiskostenvergoeding),
      d.yearSaldo !== null ? round2(d.yearSaldo) : '',
      round2(potjeBefore),
      round2(potjeMutation),
      round2(potjeAfter),
    ];
  });

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  // Kolombreedte: medewerkernummer + naam wat ruimer, rest standaard
  ws['!cols'] = [
    { wch: 18 }, { wch: 24 }, { wch: 10 }, { wch: 10 }, { wch: 12 },
    { wch: 14 }, { wch: 13 }, { wch: 13 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
    { wch: 16 }, { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 12 },
    { wch: 10 }, { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 14 }, { wch: 12 },
  ];

  // Getalcellen: forceer 2 decimalen voor uur/euro-kolommen (kolommen F-P, R-V)
  const numericCols = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 17, 18, 19, 20, 21];
  for (let r = 1; r <= rows.length; r++) {
    for (const c of numericCols) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = ws[addr];
      if (cell && typeof cell.v === 'number') cell.z = '0.00';
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, period.label.slice(0, 31));

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  return new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/**
 * Trigger xlsx-download in browser.
 */
export function downloadExcel(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
